import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { pt } from "@/i18n/pt";
import { en } from "@/i18n/en";
import { es } from "@/i18n/es";
import { it } from "@/i18n/it";
import type { Translations } from "@/i18n/pt";
import { useAuthContext } from "@/contexts/AuthContext";
import { translateCategoryName } from "@/i18n/categories";
import { uiByLanguage, type UiStrings } from "@/i18n/ui";

export type Language = "pt" | "en" | "es" | "it";
export type Currency = "BRL" | "EUR" | "USD" | "GBP";

const translations: Record<Language, Translations> = { pt, en, es, it };

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  BRL: "R$",
  EUR: "€",
  USD: "$",
  GBP: "£",
};

const CURRENCY_LOCALES: Record<Currency, string> = {
  BRL: "pt-BR",
  EUR: "de-DE",
  USD: "en-US",
  GBP: "en-GB",
};

const LANGUAGE_LOCALES: Record<Language, string> = {
  pt: "pt-BR",
  en: "en-US",
  es: "es-ES",
  it: "it-IT",
};

interface I18nContextValue {
  language: Language;
  currency: Currency;
  t: Translations;
  setLanguage: (lang: Language) => void;
  setCurrency: (currency: Currency) => void;
  formatCurrency: (amount: number | string) => string;
  formatDate: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  categoryName: (name: string | null | undefined) => string;
  ui: UiStrings;
  currencySymbol: string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function detectBrowserLanguage(): Language {
  const lang = navigator.language?.toLowerCase() ?? "pt";
  if (lang.startsWith("pt")) return "pt";
  if (lang.startsWith("es")) return "es";
  if (lang.startsWith("en")) return "en";
  if (lang.startsWith("it")) return "it";
  return "pt";
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("bolso_language") as Language | null;
    return saved ?? detectBrowserLanguage();
  });

  const [currency, setCurrencyState] = useState<Currency>(() => {
    const saved = localStorage.getItem("bolso_currency") as Currency | null;
    return saved ?? "BRL";
  });

  // ── Sync with server preferences ──────────────────────────────────────────
  // IMPORTANT: Do NOT call trpc.auth.me.useQuery() here — that would create a
  // second concurrent query instance alongside AuthProvider's query, causing
  // the insertBefore DOM corruption error in production (React 19 + React Router).
  //
  // I18nProvider is mounted INSIDE AuthProvider (see App.tsx), so useAuthContext()
  // reads the single shared auth state with zero extra network requests.
  const { user } = useAuthContext();

  // Sync language/currency whenever the authenticated user data changes
  useEffect(() => {
    if (!user) return;

    if (user.currency && ["BRL", "EUR", "USD", "GBP"].includes(user.currency)) {
      const serverCurrency = user.currency as Currency;
      setCurrencyState(serverCurrency);
      localStorage.setItem("bolso_currency", serverCurrency);
    }
    if (user.language && ["pt", "en", "es", "it"].includes(user.language)) {
      const serverLanguage = user.language as Language;
      setLanguageState(serverLanguage);
      localStorage.setItem("bolso_language", serverLanguage);
    }
  }, [user?.currency, user?.language]); // re-runs whenever user prefs change

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("bolso_language", lang);
  }, []);

  const setCurrency = useCallback((cur: Currency) => {
    setCurrencyState(cur);
    localStorage.setItem("bolso_currency", cur);
  }, []);

  const formatCurrency = useCallback(
    (amount: number | string): string => {
      const num = typeof amount === "string" ? parseFloat(amount) : amount;
      if (isNaN(num)) return `${CURRENCY_SYMBOLS[currency]} 0,00`;
      try {
        return new Intl.NumberFormat(CURRENCY_LOCALES[currency], {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(num);
      } catch {
        return `${CURRENCY_SYMBOLS[currency]} ${num.toFixed(2)}`;
      }
    },
    [currency]
  );

  const formatDate = useCallback(
    (date: Date | string | number, options: Intl.DateTimeFormatOptions = {}) => {
      const parsedDate = new Date(date);
      if (Number.isNaN(parsedDate.getTime())) return "";
      return new Intl.DateTimeFormat(LANGUAGE_LOCALES[language], options).format(parsedDate);
    },
    [language]
  );

  const categoryName = useCallback(
    (name: string | null | undefined) => translateCategoryName(name, language),
    [language]
  );

  const value: I18nContextValue = {
    language,
    currency,
    t: translations[language],
    setLanguage,
    setCurrency,
    formatCurrency,
    formatDate,
    categoryName,
    ui: uiByLanguage[language],
    currencySymbol: CURRENCY_SYMBOLS[currency],
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
